<script setup lang="ts">
import "chartjs-adapter-date-fns";

import {
  BarElement,
  Chart,
  type ChartData,
  type ChartOptions,
  Colors,
  LinearScale,
  TimeScale,
  Tooltip
} from "chart.js";
import { eachMonthOfInterval, getTime, isSameMonth, max, min, subMonths } from "date-fns";
import { computed } from "vue";
import { Bar as ChartBar } from "vue-chartjs";

import type { FilterCriteria } from "@/components/QuestionnaireResponsesFilter.vue";
import type { QuestionnaireWithResponses } from "@/types";

const { questionnaires, filter } = defineProps<{
  questionnaires: QuestionnaireWithResponses[];
  filter?: FilterCriteria;
}>();

Chart.register(LinearScale, TimeScale, BarElement, Tooltip, Colors);

const months = computed(() => {
  const responseDates = questionnaires.flatMap((questionnaire) =>
    questionnaire.responses.map((response) => response.createdAt)
  );

  return eachMonthOfInterval({ start: min(responseDates), end: max(responseDates) }).map((date) => getTime(date));
});

const chartData = computed<ChartData<"bar", { x: number; y: number }[]>>(() => ({
  datasets: questionnaires.map((questionnaire) => ({
    label: questionnaire.name,
    data: months.value.map((timestamp) => ({
      x: timestamp,
      y: questionnaire.responses.filter((response) => isSameMonth(response.createdAt, timestamp)).length
    })),
    hidden: filter && !filter.questionnaireIds.includes(questionnaire.id),
    backgroundColor: questionnaire.color,
    borderRadius: 2
  }))
}));

const chartOptions = computed<ChartOptions<"bar">>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  parsing: false,
  normalized: true,
  scales: {
    x: {
      axis: "x",
      type: "time",
      stacked: true,
      time: {
        unit: "month",
        round: "month",
        displayFormats: { month: "MMM yy" },
        tooltipFormat: "MMMM yyyy"
      },
      suggestedMin: getTime(subMonths(new Date(), 6)),
      max: getTime(new Date())
    },
    y: {
      beginAtZero: true,
      grace: 1,
      stacked: true,
      ticks: {
        stepSize: 1,
        callback: (value) => value.toLocaleString("da-DK")
      }
    }
  },
  plugins: {
    tooltip: { boxPadding: 4 }
  }
}));
</script>

<template>
  <div style="height: 400px">
    <ChartBar :data="chartData" :options="chartOptions" />
  </div>
</template>
